import React from 'react';
import HeaderOne from '../header/HeaderOne';
import FooterOne from '../footer/FooterOne';
import Head from '../components/Head';

export default function Roadmap() {
  const roadmapItems = [
    {
      date: 'End of February',
      status: 'in-progress',
      title: 'Enhanced SMS Warmup',
      description: 'Instantly.ai-style warmup with smart algorithms and analytics.',
      features: [
        'Adaptive message frequency',
        'Warmup health score',
        'Detailed analytics',
      ],
    },
    {
      date: 'End of March',
      status: 'planned',
      title: 'AI SDR Agent',
      description: 'AI-powered sales rep for lead qualification and booking.',
      features: [
        'Natural conversations',
        'Calendar integration',
        'Smart follow-ups',
      ],
    },
    {
      date: 'End of April',
      status: 'planned',
      title: 'HubSpot Integration',
      description: 'Native two-way sync with HubSpot CRM.',
      features: [
        'Contact sync',
        'Workflow automation',
        'Conversation logging',
      ],
    },
    {
      date: 'Q2 2026',
      status: 'planned',
      title: 'Salesforce Integration',
      description: 'Full Salesforce CRM integration with SMS automation.',
      features: [
        'Lead & contact sync',
        'Campaign triggers',
        'Activity tracking',
      ],
    },
    {
      date: 'Q2 2026',
      status: 'planned',
      title: 'Zapier Integration',
      description: 'Connect TextLink to 5,000+ apps via Zapier.',
      features: [
        'Send SMS triggers',
        'Receive SMS actions',
        'Multi-step workflows',
      ],
    },
    {
      date: 'Q3 2026',
      status: 'exploring',
      title: 'Pipedrive Integration',
      description: 'SMS directly from Pipedrive deals and contacts.',
      features: [
        'Deal-based messaging',
        'Contact sync',
        'Activity logging',
      ],
    },
  ];

  const getStatusStyle = (status) => {
    const styles = {
      'in-progress': { bg: '#dcfce7', color: '#15803d', dot: '#22c55e', label: 'In Progress' },
      'planned': { bg: '#e0e7ff', color: '#4338ca', dot: '#6366f1', label: 'Planned' },
      'exploring': { bg: '#fef3c7', color: '#92400e', dot: '#f59e0b', label: 'Exploring' },
    };
    return styles[status] || styles.planned;
  };

  return (
    <>
      <Head
        title="Roadmap — What We're Building Next"
        description="See what's coming to TextLink: enhanced SMS warmup, AI SDR agent, HubSpot integration, Salesforce, Zapier, Pipedrive, and more."
        canonical="/roadmap"
      />
      <HeaderOne />
      <main className="roadmap-page">
        <section className="roadmap-section">
          <div className="container">
            {/* Header */}
            <div className="page-header">
              <span className="badge">Roadmap</span>
              <h1>What's Next</h1>
              <p>Features we're building based on your feedback.</p>
            </div>

            {/* Timeline */}
            <div className="timeline">
              {roadmapItems.map((item, index) => {
                const statusStyle = getStatusStyle(item.status);
                return (
                  <div key={index} className="timeline-item">
                    <div className="timeline-line">
                      <div 
                        className="dot" 
                        style={{ background: statusStyle.dot }}
                      />
                      {index < roadmapItems.length - 1 && <div className="line" />}
                    </div>
                    
                    <div className="card">
                      <div className="card-top">
                        <div className="meta">
                          <span className="date">{item.date}</span>
                          <span 
                            className="status"
                            style={{ 
                              background: statusStyle.bg, 
                              color: statusStyle.color 
                            }}
                          >
                            {statusStyle.label}
                          </span>
                        </div>
                      </div>
                      
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                      
                      <div className="features">
                        {item.features.map((f, i) => (
                          <span key={i} className="feature">
                            <span className="check">✓</span> {f}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* CTA */}
            <div className="cta">
              <span>Have a feature request?</span>
              <a href="/contact">Share feedback →</a>
            </div>
          </div>
        </section>
      </main>
      <FooterOne />

      <style jsx>{`
        .roadmap-page {
          background: #fff;
          min-height: 100vh;
        }

        .roadmap-section {
          padding: 9rem 0 4rem;
        }

        .container {
          max-width: 640px;
          margin: 0 auto;
          padding: 0 1.25rem;
        }

        /* Header */
        .page-header {
          text-align: center;
          margin-bottom: 3rem;
        }

        .badge {
          display: inline-block;
          background: #f4f4f5;
          color: #52525b;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 500;
          margin-bottom: 0.75rem;
          border: 1px solid #e4e4e7;
        }

        .page-header h1 {
          font-size: 2rem;
          font-weight: 700;
          color: #18181b;
          margin: 0 0 0.5rem;
          letter-spacing: -0.03em;
        }

        .page-header p {
          font-size: 0.9375rem;
          color: #71717a;
          margin: 0;
        }

        /* Timeline */
        .timeline {
          display: flex;
          flex-direction: column;
        }

        .timeline-item {
          display: flex;
          gap: 1rem;
        }

        .timeline-line {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 12px;
          flex-shrink: 0;
        }

        .dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .line {
          width: 1.5px;
          flex: 1;
          background: #e4e4e7;
          margin: 6px 0;
        }

        /* Card */
        .card {
          flex: 1;
          background: #fafafa;
          border: 1px solid #e4e4e7;
          border-radius: 10px;
          padding: 1rem;
          margin-bottom: 1rem;
          transition: all 0.15s ease;
        }

        .card:hover {
          background: #fff;
          border-color: #d4d4d8;
        }

        .card-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.625rem;
        }

        .meta {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .date {
          font-size: 0.75rem;
          font-weight: 600;
          color: #3f3f46;
        }

        .status {
          font-size: 0.625rem;
          font-weight: 600;
          padding: 0.2rem 0.5rem;
          border-radius: 10px;
          text-transform: uppercase;
          letter-spacing: 0.02em;
        }

        .card h3 {
          font-size: 1rem;
          font-weight: 600;
          color: #18181b;
          margin: 0 0 0.25rem;
          letter-spacing: -0.01em;
        }

        .card p {
          font-size: 0.8125rem;
          color: #71717a;
          margin: 0 0 0.75rem;
          line-height: 1.4;
        }

        .features {
          display: flex;
          flex-wrap: wrap;
          gap: 0.375rem;
        }

        .feature {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.6875rem;
          color: #52525b;
          background: #fff;
          border: 1px solid #e4e4e7;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
        }

        .check {
          color: #22c55e;
          font-size: 0.625rem;
          font-weight: 600;
        }

        /* CTA */
        .cta {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          margin-top: 2rem;
          padding-top: 1.5rem;
          border-top: 1px solid #e4e4e7;
        }

        .cta span {
          font-size: 0.8125rem;
          color: #71717a;
        }

        .cta a {
          font-size: 0.8125rem;
          font-weight: 600;
          color: #18181b;
          text-decoration: none;
          transition: color 0.15s ease;
        }

        .cta a:hover {
          color: #6366f1;
        }

        /* Mobile */
        @media (max-width: 480px) {
          .roadmap-section {
            padding: 7rem 0 3rem;
          }

          .page-header {
            margin-bottom: 2rem;
          }

          .page-header h1 {
            font-size: 1.5rem;
          }

          .timeline-item {
            gap: 0.75rem;
          }

          .card {
            padding: 0.875rem;
          }

          .card h3 {
            font-size: 0.9375rem;
          }

          .cta {
            flex-direction: column;
            gap: 0.25rem;
          }
        }
      `}</style>
    </>
  );
}
